<template>
  <div>
    <Molded class="cost-center-list-header" :paddingTop="16" :paddingRight="20" :paddingBottom="16" :paddingLeft="20">
      <div class="cost-center-list-toolbar">
        <Button _key="btncCostCenterCreate" title="Centro de Custo" classIcon="fas fa-plus-circle" type="primary"
          size="small" eventName="costCenterCreate" />
      </div>

      <div class="cost-center-list-filter">
        <div class="cost-center-list-filter-search">
          <Search />
        </div>
      </div>

      <div class="cost-center-list-chips-bar">
        <div class="cost-center-list-chips">
          <span v-for="option in typeOptions" :key="option.id" class="cost-center-list-chip"
            :class="{ 'is-active': typeFilter === option.id }" @click="selectType(option.id)">
            {{ option.title }}
          </span>
        </div>
        <TotalRecordsBadge :total="filteredTotal" />
      </div>
    </Molded>

    <Loading v-if="loading" type="line" :center="false" class="cost-center-list-loading" />

    <div v-if="!loading && filteredTotal === 0" class="cost-center-list-empty">Nenhuma categoria encontrada</div>

    <div class="cost-center-list-grid">
      <CostCenterCard v-for="costCenter in visibleData" :key="costCenter.id" :costCenter="costCenter"
        :selected="isSelected(costCenter.id)" :expanded="expandedCostCenterId === costCenter.id"
        @edit="updateCostCenter" @delete="openDeleteModal" @select="toggleSelect" @toggle="toggleChartOfAccounts">
        <div slot="chart">
          <ChartOfAccountsCreateUpdateList :costCenter="costCenter" />
        </div>
      </CostCenterCard>
    </div>

    <LoadingMoreButton :remaining="filteredTotal - visibleData.length" @click="loadMore" />

    <BottomActionsBar :selected="selected" @close="addSelected([])">
      <ActionsSelected :selected="selected" :actions="actions" />
    </BottomActionsBar>

    <Modal title="Centro de Custo" :width="500" :height="250" v-show="showModal('costCenterCreateUpdate')">
      <CostCenterCreateUpdate />
    </Modal>

    <Modal title="Excluir Categoria" :width="450" v-if="showModal('costCenterDelete')">
      <div class="text-center">
        <p class="cost-center-list-delete-text">Tem certeza que deseja excluir esta categoria?</p>
        <Confirmation :isModal="false" title="Deseja excluir?" type="danger" :confirmed="remove" />
      </div>
    </Modal>
  </div>
</template>

<script>

import Button from "@/ui/forms/Button";
import Modal from "@/ui/forms/Modal";

import BottomActionsBar from "@/ui/layout/BottomActionsBar";
import Molded from "@/ui/layout/Molded";
import TotalRecordsBadge from "@/ui/layout/TotalRecordsBadge.vue";

import ActionsSelected from "@/ui/shared/actions/ActionsSelected.vue";
import Confirmation from "@/ui/shared/Confirmation.vue";
import Loading from "@/ui/shared/Loading.vue";
import LoadingMoreButton from "@/ui/shared/LoadingMoreButton.vue";
import Search from "@/ui/shared/Search.vue";

import CostCenterCard from "./CostCenterCard.vue";
import CostCenterCreateUpdate from "./CostCenterCreateUpdate.vue";
import ChartOfAccountsCreateUpdateList from "../chartOfAccounts/ChartOfAccountsCreateUpdateList.vue";

import CostCenterService from "../../services/costCenter.service";

import { mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "CostCenterList",
  components: {
    ActionsSelected,
    BottomActionsBar,
    Button,
    Confirmation,
    CostCenterCard,
    CostCenterCreateUpdate,
    Loading,
    LoadingMoreButton,
    Modal,
    Molded,
    Search,
    TotalRecordsBadge,
    ChartOfAccountsCreateUpdateList,
  },
  data() {
    return {
      loading: true,
      content: { data: [], totalRecords: 0 },
      expandedCostCenterId: "",
      costCenterToDelete: {},
      typeFilter: "all",
      typeOptions: [
        { id: "all", title: "Todos" },
        { id: "revenue", title: "Receita" },
        { id: "expense", title: "Despesa" },
      ],
      visibleCount: 12,
      loadMoreStep: 3,
      actions: [
        { type: "delete", title: "Remover", description: "Remover", url: CostCenterService.urlDelete, method: "post" },
      ],
    };
  },
  computed: {
    ...mapGetters("generic", ["showModal", "event"]),
    ...mapState("generic", ["search", "executedSearch", "clearedSearch"]),
    filteredData() {
      if (this.typeFilter === "all") return this.content.data;
      const typeName = this.typeFilter === "revenue" ? "Receita" : "Despesa";
      return this.content.data.filter((costCenter) => costCenter.typeName === typeName);
    },
    filteredTotal() {
      return this.filteredData.length;
    },
    visibleData() {
      return this.filteredData.slice(0, this.visibleCount);
    },
    selected: {
      get() {
        return this.$store.state.generic.selected;
      },
      set(value) {
        this.addSelected(value);
      },
    },
  },
  mounted() {
    this.getAll();
  },
  beforeDestroy() {
    this.updateSearch({ content: "", filter: { content: "Contém", id: "contains" } });
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "addSelected", "openModal", "hideModal", "removeLoading", "updateSearch"]),
    isSelected(id) {
      return this.selected.includes(id);
    },
    toggleSelect(costCenter) {
      const id = costCenter.id;
      this.selected = this.isSelected(id)
        ? this.selected.filter((selectedId) => selectedId !== id)
        : [...this.selected, id];
    },
    selectType(id) {
      if (this.typeFilter === id) return;
      this.typeFilter = id;
      this.visibleCount = 12;
      this.expandedCostCenterId = "";
    },
    loadMore() {
      this.visibleCount += this.loadMoreStep;
    },
    async getAll() {
      this.loading = true;
      try {
        const response = await CostCenterService.getAll({
          noPaginated: true,
          search: this.search.content,
          filter: this.search.filter.id || "contains",
        });
        this.content.data = response.content.items;
        this.content.totalRecords = response.content.totalRecords;
      } finally {
        this.loading = false;
      }
    },
    updateCostCenter(costCenter) {
      this.addEvent({ name: "costCenterUpdate", data: costCenter });
    },
    toggleChartOfAccounts(costCenter) {
      if (this.expandedCostCenterId === costCenter.id) {
        this.expandedCostCenterId = "";
        this.getAll();
      } else {
        this.expandedCostCenterId = costCenter.id;
      }
    },
    openDeleteModal(costCenter) {
      this.costCenterToDelete = costCenter;
      this.openModal("costCenterDelete");
    },
    async remove() {
      try {
        await CostCenterService.delete([this.costCenterToDelete.id]);
        this.hideModal();
        this.expandedCostCenterId = "";
        await this.getAll();
      } catch (error) {
        console.error(error);
      } finally {
        this.removeLoading(["confirm", "notConfirm"]);
      }
    },
  },
  watch: {
    executedSearch: function () {
      this.visibleCount = 12;
      this.getAll();
      this.removeLoading(["btnSearch"]);
    },
    clearedSearch: function () {
      this.visibleCount = 12;
      this.getAll();
      this.removeLoading(["btnClean"]);
    },
    event: {
      deep: true,
      handler(event) {
        if (event.name === "costCenterSaved") {
          this.getAll();
          return;
        }

        if (event.name === "costCenterCreate" || event.name === "costCenterUpdate") {
          this.openModal("costCenterCreateUpdate");
          this.removeLoading(["btncCostCenterCreate"]);
        }

        if (event.name === "deselectAll") {
          this.addSelected([]);
        }
      },
    },
  },
};
</script>

<style scoped>
.cost-center-list-header {
  margin-bottom: 16px;
}

.cost-center-list-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cost-center-list-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0;
}

.cost-center-list-filter> :last-child {
  flex-shrink: 0;
}

.cost-center-list-filter-search {
  flex: 1 1 320px;
  min-width: 0;
}

.cost-center-list-filter-search>>>.margin {
  padding-top: 0;
}

.cost-center-list-filter-search>>>.div-button {
  padding-top: 0;
}

.cost-center-list-chips-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.cost-center-list-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cost-center-list-chip {
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid var(--border-color);
  background: var(--bg-base);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.cost-center-list-chip:hover {
  background: var(--bg-hover);
}

.cost-center-list-chip.is-active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--text-inverse);
}

.cost-center-list-loading {
  margin-bottom: 12px;
}

.cost-center-list-empty {
  padding: 48px 24px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.cost-center-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  align-items: start;
}

.cost-center-list-delete-text {
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 14px;
}

.cost-center-list-delete-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-elevated);
  margin-bottom: 20px;
}

.cost-center-list-delete-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}

.cost-center-list-delete-description {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}
</style>
